import { Grid2, Step, StepButton, StepLabel, Stepper } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FormStatusEnum } from "../../_common/enum/forms/formStatus.enum";
import { IFormManage } from "../../_common/models/form";
import { StandardButton } from "../../_components/button/primary.button";
import { toastr } from "../../_components/toastr/toastr";
import { getStringDateNormalized } from "../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { accountService } from "../../_services/accounts.service";
import { formService } from "../../_services/form.service";
import { FormSectionManage } from "./formSection/formSection-manage";
import { tokenService } from "../../_services/token.service";
import { API_URL } from "../../_config/config.const";
import { FormSectionView } from "./formSection/formSection-view";

export function FormManage() {

    const { formId } = useParams<{ formId: string }>();
    const _formId = Number(formId);

    const [data, setData] = useState<IFormManage>();

    const isReadOnly = !!data && !data.isFormOwner && !accountService.isAdmin && !accountService.isManager;

    const [loading, setLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);

    const [activeStep, setActiveStep] = React.useState<number | null>(null);
    const [stepWithLatestId, setStepWithLatestId] = React.useState<number | null>(null);

    useEffect(() => {
        fetchData();
    }, [formId]);

    useEffect(() => {
        if (data) {
            if (activeStep === null || shouldShowReviewSection()) {
                resetActiveStep();
            }

            resetStepWithLatestId();
        }
    }, [data]);

    function fetchData(isOnSave?: boolean) {

        setLoading(true);
        return formService
            .get(_formId)
            .then(
                (json) => {
                    setData(json);
                }
            )
            .finally(() => {
                setLoading(false);
                if (isOnSave == true) {
                    setIsSaving(false);
                }
            });
    }

    function completeForm() {
        setIsSaving(true);

        return formService
            .complete(_formId)
            .then(
                (json) => {
                    toastr.success("Form successfully completed");
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => {
                setIsSaving(false);
            });
    }

    function approveForm() {
        setIsSaving(true);

        return formService
            .approve(_formId)
            .then(
                (json) => {
                    toastr.success("success");
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => {

                setIsSaving(false);
            });
    }

    function returnForm() {
        setIsSaving(true);

        return formService
            .returnForm(_formId)
            .then(
                (json) => {
                    toastr.success("success");
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => {
                setIsSaving(false);

            });
    }


    function onSave() {
        fetchData();
        triggerNextStep(true);
    }

    function triggerNextStep(isOnSave?: boolean) {
        if (activeStep == getActiveStep()) {
            ///console.log("asd");
            setActiveStep(activeStep + 1);
            setStepWithLatestId(((stepWithLatestId || 0) + 1));
        }
        else {
            setActiveStep(getActiveStep());
        }

        fetchData(isOnSave);
    }

    function isTerminalStatus(statusId?: FormStatusEnum) {
        return statusId === FormStatusEnum.Approved
            || statusId === FormStatusEnum.Cancelled
            || statusId === FormStatusEnum.Withdrawn;
    }

    function getActiveStep() {
        let step: number | null = null;

        if ((isTerminalStatus(data?.formStatusId) || isReadOnly) && data && data.formSectionList.length > 0) {
            return data.formSectionList.length;
        }

        if (shouldShowReviewSection()) {
            return data?.formSectionList.length || 0;
        }

        if (data && data.formSectionList.length > 0) {

            data.formSectionList.forEach((item, index) => {
                if (!item.id && step === null) {
                    step = index;
                }
            })
        }
        return step || 0;
    }

    function resetActiveStep() {
        setActiveStep(getActiveStep());
    }

    function resetStepWithLatestId() {

        let step: number | null = null;
        if (data && data.formSectionList.length > 0) {
            data.formSectionList.forEach((item, index) => {
                if (item.id) {
                    step = index;
                }
            })
        }
        setStepWithLatestId(step);
    }

    function shouldShowReviewSection() {

        let shouldShowReview = false;
        if (data && data.formSectionList.length > 0) {
            shouldShowReview = !data.formSectionList.some((item, index) => {
                return !item.id
            })
        }
        return shouldShowReview;

    }

    const isCancel = accountService.isAdmin || accountService.isManager;

    function withdraw() {
        const confirmMessage = isCancel
            ? "Are you sure you want to cancel this form?"
            : "Are you sure you want to withdraw this form?";
        if (window.confirm(confirmMessage) == true) {
            setIsSaving(true);
            const status = isCancel ? FormStatusEnum.Cancelled : FormStatusEnum.Withdrawn;
            formService.disable(_formId, status).then(
                () => {
                    toastr.success(isCancel ? "Cancelled successfully" : "Withdrawn successfully");
                    fetchData();
                },
                handleErrorResponse
            ).finally(() => {
                setIsSaving(false)
            });

        }
    }


    const isReviewSection = activeStep === data?.formSectionList.length;
    const isTerminal = isTerminalStatus(data?.formStatusId);
    const isAwaitingApproval = data?.formStatusId === FormStatusEnum.CompletedAwaitingApproval
        || data?.formStatusId === FormStatusEnum.AwaitingApproval;

    return (
        <>
            {(activeStep !== null && !!data && data.formSectionList.length > 0) &&
                <>

                    <h2 style={{ textAlign: "center", marginBottom: "8px" }}>{data.name}</h2>

                    {!isReadOnly &&
                        <div style={{ width: "100%", textAlign: "center", marginBottom: "16px", paddingBottom: "16px" }}>
                            <i>Your progress to this point has been saved. You may access this form at any time before submission.</i>
                        </div>
                    }

                    {data.dateCompleted &&
                        <h6 style={{ width: "100%", textAlign: "center" }}>Completed Date: {getStringDateNormalized(data.dateCompleted, true)}</h6>
                    }

                    <Stepper style={{ "paddingTop": "16px !important" }} activeStep={activeStep} alternativeLabel >


                        {data.formSectionList.map((listItem, index) => {
                            const stepProps: { completed?: boolean } = {};
                            stepProps.completed = (listItem.id || 0) > 0 || index < activeStep;

                            return (
                                <Step key={index + "_" + listItem.id} {...stepProps}>
                                    <StepButton disabled={isTerminal || isReadOnly || stepWithLatestId === null || (!listItem.id && (stepWithLatestId + 1) !== index)} onClick={() => { setActiveStep(index) }}>
                                        <StepLabel>{listItem.name}</StepLabel>
                                    </StepButton>
                                </Step>
                            );
                        })}

                        <Step key={data.formSectionList.length} disabled={false}>
                            <StepButton disabled={!isTerminal && !isReadOnly && !shouldShowReviewSection()} onClick={() => { setActiveStep(data.formSectionList.length) }}>
                                <StepLabel>Review</StepLabel>
                            </StepButton>
                        </Step>

                    </Stepper>
                </>
            }

            {(activeStep !== null && activeStep !== data?.formSectionList.length) &&
                <FormSectionView
                    formTemplateSectionId={data && data.formSectionList.length > 0 && activeStep < data.formSectionList.length ? data.formSectionList[activeStep].formTemplateSectionId : 0}
                    isSectionSaving={saving}
                    formSectionId={data?.id}
                    onSave={onSave} />
            }

            {(!!data && data.formSectionList && !!data.formSectionList.length && !!isReviewSection) && data.formSectionList.map((section, index) =>
                <FormSectionView
                    formTemplateSectionId={section.formTemplateSectionId ?? 0}
                    isForReview
                    isSectionSaving={saving}
                    formSectionId={data?.id}
                    onSave={onSave} />
            )}

            <Grid2 container spacing={1} style={{ "paddingLeft": "0px", "paddingRight": "0px" }}>

                {(!!isReviewSection && !isTerminal && !isAwaitingApproval && !isReadOnly) &&
                    <>
                        <Grid2 size={{ xs: 4 }} >
                            <StandardButton onClick={withdraw} saving={loading || saving} icon="clear" variant="error" type="button">{isCancel ? "Cancel The Form" : "Withdraw The Form"}</StandardButton>
                        </Grid2>
                        <Grid2 size={{ xs: 8 }} >
                            <StandardButton icon={"check"} loading={loading} saving={saving} onClick={completeForm} variant="success" type="button">Complete The Form</StandardButton>
                        </Grid2>
                        <br />
                        <Grid2 size={{ xs: 12 }} >
                            <StandardButton onClick={() => {
                                tokenService.GetReportToken().then((_token) => {
                                    window.open(API_URL + "/form/GetFile/?token=" + _token + "&formId=" + _formId);
                                });
                            }} saving={saving} loading={loading} icon="download" variant="primary" type="button">Download File</StandardButton>
                        </Grid2 >
                    </>
                }

                {(!!isReviewSection && !isTerminal && (!!accountService.isAdmin || !!accountService.isManager) && isAwaitingApproval) &&
                    <>
                        <Grid2 size={{ xs: 4 }} >
                            <StandardButton onClick={withdraw} saving={loading || saving} icon="clear" variant="error" type="button">Cancel The Form</StandardButton>
                        </Grid2 >
                        <Grid2 size={{ xs: 4 }}>
                            <StandardButton icon={"block"} loading={loading} saving={saving} onClick={returnForm} variant="warning" type="button">Return Form</StandardButton>
                        </Grid2>
                        <Grid2 size={{ xs: 4 }}>
                            <StandardButton icon={"check"} loading={loading} saving={saving} onClick={approveForm} variant="success" type="button">Approve Form</StandardButton>
                        </Grid2>
                        <br />
                        <Grid2 size={{ xs: 12 }} >
                            <StandardButton onClick={() => {
                                tokenService.GetReportToken().then((_token) => {
                                    window.open(API_URL + "/form/GetFile/?token=" + _token + "&formId=" + _formId);
                                });
                            }} saving={saving} loading={loading} icon="download" variant="primary" type="button">Download File</StandardButton>
                        </Grid2 >
                    </>
                }

                {(!!isReviewSection && isTerminal) &&
                    <Grid2 size={{ xs: 12 }} >
                        <StandardButton onClick={() => {
                            tokenService.GetReportToken().then((_token) => {
                                window.open(API_URL + "/form/GetFile/?token=" + _token + "&formId=" + _formId);
                            });
                        }} saving={saving} loading={loading} icon="download" variant="primary" type="button">Download File</StandardButton>
                    </Grid2 >
                }


            </Grid2 >

        </>
    );
}